<template>
    <div class="zebra-editor-wrapper">
        <!-- Elegant floating capsule header in the top center -->
        <div class="zebra-editor-floating-header" :class="editorTheme">
            <v-icon small class="mr-0"
                :color="editorTheme === 'dark' ? 'white' : 'grey darken-3'">mdi-file-document-edit</v-icon>
            <span class="body-2 font-weight-medium text-no-wrap"
                :class="editorTheme === 'dark' ? 'white--text' : 'grey--text text--darken-4'">Editor de Etiquetas
                Sunexpert (ZPL)</span>

            <v-btn icon x-small :color="editorTheme === 'dark' ? 'white' : 'grey darken-3'" @click="triggerPrintModal"
                class="ml-24 mr-1" title="Imprimir (Ctrl + P)">
                <v-icon small>mdi-printer</v-icon>
            </v-btn>

            <v-btn v-if="ShowSave" icon x-small :color="editorTheme === 'dark' ? 'white' : 'grey darken-3'"
                @click="saveTemplate" class="ml-8 mr-1" title="Guardar">
                <v-icon small>mdi-content-save</v-icon>
            </v-btn>

            <v-btn icon x-small :color="editorTheme === 'dark' ? 'white' : 'grey darken-3'" @click="$emit('close')"
                class="ml-8" title="Cerrar">
                <v-icon small>mdi-close</v-icon>
            </v-btn>
        </div>

        <div class="iframe-container">
            <iframe v-if="editorUrl" ref="zebraIframe" :src="editorUrl" class="zebra-editor-iframe" frameborder="0"
                @load="sendDataToEditor"></iframe>
            <v-overlay :value="loadingTemplate" absolute>
                <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
            </v-overlay>
        </div>
    </div>
</template>

<script>

export default {
    name: 'ZebraEditor',
    props: {
        labelData: {
            type: Object,
            default: () => null
        },
        ShowSave: {
            type: Boolean,
            default: false
        },
        labelCount: {
            type: Number,
            default: 1
        }
    },
    data() {
        return {
            editorUrl: '',
            loadingTemplate: false,
            // JSON de prueba manual para integración (llaves coinciden con las variables de la plantilla)
            rawLabelData: {},
            localLabelCount: this.labelCount,
            editorTheme: 'dark'
        };
    },
    watch: {
        labelCount(newVal) {
            if (this.localLabelCount !== newVal) {
                this.localLabelCount = newVal;
            }
        },
        localLabelCount(newVal) {
            this.$emit('update:label-count', newVal);
            this.sendLabelCountToEditor();
        }
    },
    computed: {
        editorRecordId() {
            if (!this.labelData) return 'default';
            return this.labelData.Id || 'default';
        },
        activeLabelData() {
            // Combinar los datos del padre con rawLabelData para asegurar que siempre haya variables de prueba,
            // priorizando cualquier variable real que el padre sí envíe.
            const result = {
                ...this.rawLabelData,
                ...(this.labelData || {})
            };

            // Limpiar metadatos técnicos para no contaminar las variables dinámicas de la etiqueta ZPL
            delete result.Template;
            delete result.Id;
            delete result.CtmpID;
            delete result.CtmpvID;
            delete result.CtmpdID;

            console.log("activeLabelData result:", JSON.parse(JSON.stringify(result)));
            return result;
        }
    },
    async mounted() {
        // Cargar tema inicial desde localStorage si existe
        try {
            const sessionRaw = localStorage.getItem('zpl-designer-session');
            const sessionParsed = sessionRaw ? JSON.parse(sessionRaw) : null;
            if (sessionParsed && sessionParsed.state && sessionParsed.state.theme) {
                this.editorTheme = sessionParsed.state.theme;
            }
        } catch (themeErr) {
            console.error("Error al leer tema inicial de localStorage:", themeErr);
        }

        window.addEventListener('message', this.handleIframeMessage);
        window.addEventListener('keydown', this.handleKeyDown);
        this.loadingTemplate = true;
        try {
            // Cargar la plantilla almacenada en la base de datos (simulada)
            const dbTemplate = await this.simulatedFetchTemplateFromDB();
            if (dbTemplate) {
                // El JSON de BD ya usa el formato nativo de "Abrir diseño".
                // Se entrega sin reconstruir ni sanitizar para conservar exactamente
                // la misma estructura que procesa el importador de ZPLab.
                window.zebraTemplateJson = dbTemplate;
            } else {
                window.zebraTemplateJson = null;
            }
        } catch (e) {
            console.error("Error al cargar la plantilla de la BD:", e);
        } finally {
            this.loadingTemplate = false;

            // Establecer la variable global y local ANTES de que el iframe comience a cargar
            window.zebraLabelData = this.activeLabelData;
            localStorage.setItem('zebra-label-data', JSON.stringify(this.activeLabelData));

            // Establecer la cantidad inicial de etiquetas
            window.zebraLabelCount = this.localLabelCount;
            localStorage.setItem('zebra-label-count', this.localLabelCount);

            const baseUrl = (typeof process !== 'undefined' && process.env && process.env.BASE_URL) || '/';
            // Add a timestamp query parameter to force iframe reload/refetch when mounted
            this.editorUrl = `${baseUrl}zebra/editor.html?t=${Date.now()}`;
        }
    },
    beforeDestroy() {
        window.removeEventListener('message', this.handleIframeMessage);
        window.removeEventListener('keydown', this.handleKeyDown);
    },
    methods: {
        handleKeyDown(e) {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
                e.preventDefault();
                this.triggerPrintModal();
            }
        },
        triggerPrintModal() {
            const iframe = this.$refs.zebraIframe;
            if (iframe && iframe.contentWindow) {
                try {
                    if (iframe.contentWindow.zebraStore) {
                        iframe.contentWindow.zebraStore.getState().openZebraPrint('label');
                    } else {
                        console.warn("Zebra store is not ready yet.");
                    }
                } catch (e) {
                    console.error("Error triggering print modal:", e);
                }
            }
        },
        sendDataToEditor() {
            const data = this.activeLabelData;
            console.log("Inyectando datos dinámicos al editor:", data);

            // 1. Exponer en el objeto global de la ventana padre
            window.zebraLabelData = data;

            // 2. Exponer en el objeto global del iframe y enviar vía postMessage
            const iframe = this.$refs.zebraIframe;
            if (iframe && iframe.contentWindow) {
                try {
                    iframe.contentWindow.zebraLabelData = data;
                    iframe.contentWindow.postMessage({
                        type: 'SET_LABEL_DATA',
                        data: data
                    }, '*');
                } catch (e) {
                    console.error("Error al inyectar datos en iframe window:", e);
                }
            }

            // 3. Guardar en localStorage para persistencia
            localStorage.setItem('zebra-label-data', JSON.stringify(data));

            // Enviar cantidad de etiquetas al editor
            this.sendLabelCountToEditor();
        },
        sendLabelCountToEditor() {
            const count = parseInt(this.localLabelCount, 10) || 1;
            console.log("Enviando cantidad de etiquetas al editor:", count);
            window.zebraLabelCount = count;
            localStorage.setItem('zebra-label-count', count);

            const iframe = this.$refs.zebraIframe;
            if (iframe && iframe.contentWindow) {
                try {
                    iframe.contentWindow.zebraLabelCount = count;
                    iframe.contentWindow.postMessage({
                        type: 'SET_LABEL_COUNT',
                        data: count,
                        count: count
                    }, '*');
                } catch (e) {
                    console.error("Error al enviar SET_LABEL_COUNT al iframe window:", e);
                }
            }
        },
        handleIframeMessage(event) {
            if (event.data && event.data.type === 'PRINT_COMPLETE') {
                console.log("Impresión completada exitosamente detectada en ZebraEditor.");
                this.$emit('print-complete', this.localLabelCount);
            } else if (event.data && event.data.type === 'SET_LABEL_COUNT') {
                const count = parseInt(event.data.data || event.data.count, 10) || 1;
                if (this.localLabelCount !== count) {
                    this.localLabelCount = count;
                }
            } else if (event.data && event.data.type === 'THEME_CHANGED') {
                this.editorTheme = event.data.theme || 'dark';
            }
        },
        calcularVoiceCode(gtin, lote, fechaFormateada) {

            const CreateCrc16 = () => {
                let polynomial, value, temp;
                let table = new Array(256);
                polynomial = 40961;
                for (let i = 0; i < table.length; ++i) {
                    value = 0;
                    temp = i;
                    for (let j = 0; j < 8; ++j) {
                        if (0 != ((value ^ temp) & 1)) {
                            value = (value >> 1) ^ polynomial
                        } else {
                            value >>= 1
                        }
                        temp >>= 1
                    }
                    table[i] = value
                }
                return table
            }

            // 1. Crear la tabla CRC16 (estándar PTI)
            let crc = 0;
            const table = CreateCrc16();
            const testString = `${gtin}${lote}${fechaFormateada}`;

            for (let i = 0; i < testString.length; ++i) {
                const index = (crc ^ testString.charCodeAt(i)) % 256;
                crc = ((crc >> 8) ^ table[index]) & 0xFFFF;
            }

            // 2. Obtener los 4 dígitos con relleno de ceros
            const vc = String(crc % 10000).padStart(4, '0');

            return {
                fullCode: vc,
                minor: vc.substr(0, 2), // Ej: "04"
                major: vc.substr(2, 2)  // Ej: "82"
            };
        },
        async saveTemplate() {
            try {
                this.loadingTemplate = true;
                const sessionRaw = localStorage.getItem('zpl-designer-session');
                if (sessionRaw) {
                    const session = JSON.parse(sessionRaw);
                    if (session && session.state) {
                        // Replicar el formato del comando nativo "Guardar diseño".
                        // No persistir propiedades internas de Zustand en la plantilla.
                        const templateJsonString = this.serializeZebraDesign(session.state);
                        console.log("JSON de la plantilla usado para guardar en BD:", templateJsonString);

                        // Convertir a string para almacenar en el campo NVARCHAR(MAX)
                        // const templateJsonString = JSON.stringify(design);

                        // Guardar en Base de Datos (Simulado)
                        // await this.simulatedSaveTemplateToDB(templateJsonString);

                        // Emitir evento al padre con el JSON string por si necesita guardar el registro completo
                        if (this.ShowSave) {
                            this.$emit('save-template', templateJsonString);
                        }

                        // Mostrar alerta de éxito
                        // if (this.$fun && this.$fun.alert) {
                        //     this.$fun.alert("Plantilla guardada correctamente en la Base de Datos (Simulación).", "success");
                        // } else {
                        //     alert("Plantilla guardada correctamente en la Base de Datos (Simulación).");
                        // }
                    } else {
                        console.warn("No se encontró el estado de la plantilla en la sesión.");
                    }
                } else {
                    console.warn("No se encontró ninguna sesión del editor de Zebra.");
                }
            } catch (e) {
                console.error("Error al guardar la plantilla:", e);
                if (this.$fun && this.$fun.alert) {
                    this.$fun.alert("Error al guardar la plantilla en la BD", "error");
                }
            } finally {
                this.loadingTemplate = false;
            }
        },
        serializeZebraDesign(state) {
            const design = {
                schemaVersion: 4,
                label: state.label,
                pages: state.pages
            };

            if (Array.isArray(state.variables) && state.variables.length > 0) {
                design.variables = state.variables;
            }

            if (state.columnMapping) {
                design.csvMapping = state.columnMapping;
            }

            if (state.dataSourceRef) {
                design.dataSource = state.dataSourceRef;
            }

            return JSON.stringify(design, null, 2);
        },
        sanitizeZebraDesign(design) {
            if (!design || typeof design !== 'object') return design;

            const normalized = JSON.parse(JSON.stringify(design));
            if (!Array.isArray(normalized.pages)) return normalized;

            normalized.pages = normalized.pages.map(page => this.sanitizeZebraPage(page, normalized.variables || []));

            return normalized;
        },
        sanitizeZebraPage(page, variables = []) {
            if (!page || typeof page !== 'object' || !Array.isArray(page.objects)) return page;

            const objects = page.objects.map(object => this.sanitizeZebraObject(object, variables));
            const reversedTextBounds = objects
                .filter(object => object && object.type === 'text' && object.props && object.props.reverse === true)
                .map(object => this.getObjectBounds(object))
                .filter(Boolean);

            return {
                ...page,
                objects: objects.filter(object => !this.isReverseBackgroundArtifact(object, reversedTextBounds))
            };
        },
        sanitizeZebraObject(object, variables = []) {
            if (!object || typeof object !== 'object') return object;

            const normalized = { ...object };
            if (normalized.type === 'group' && Array.isArray(normalized.children)) {
                normalized.children = normalized.children.map(child => this.sanitizeZebraObject(child, variables));
            }

            const props = normalized.props;
            if (normalized.type === 'text' && props && props.reverse === true) {
                const content = String(props.content || '');
                const isSingleLine = !content.includes('\n');

                // ZPLab needs bounded block metrics for reversed text. Without them,
                // it can rehydrate ^FR as a large graphic background in the editor.
                if (isSingleLine) {
                    const safeWidth = this.getReverseTextBlockWidth(props, variables);
                    normalized.props = { ...props };
                    normalized.props.blockWidth = safeWidth;
                    normalized.props.blockLines = 1;
                    normalized.props.blockLineSpacing = 0;
                    normalized.props.blockJustify = normalized.props.blockJustify || 'L';
                }
            }

            return normalized;
        },
        isReverseBackgroundArtifact(object, reversedTextBounds) {
            if (!object || object.type !== 'line' || !object.props || reversedTextBounds.length === 0) return false;

            const props = object.props;
            const isHorizontal = Number(props.angle || 0) === 0;
            const isBlack = !props.color || props.color === 'B' || String(props.color).toLowerCase() === 'black';
            const thickness = Number(props.thickness || 0);
            const length = Number(props.length || 0);

            if (!isHorizontal || !isBlack || thickness < 20 || length < 20) return false;

            const bounds = this.getObjectBounds(object);
            if (!bounds) return false;

            return reversedTextBounds.some(textBounds => this.boundsOverlap(bounds, textBounds));
        },
        getObjectBounds(object) {
            if (!object || !object.props) return null;

            const x = Number(object.x || 0);
            const y = Number(object.y || 0);
            const props = object.props;

            if (object.type === 'text') {
                const width = Number(props.blockWidth || props.fontWidth || props.fontHeight || 0);
                const height = Number(props.fontHeight || 0) * Number(props.blockLines || 1);
                return { x, y, width, height };
            }

            if (object.type === 'line') {
                const angle = Number(props.angle || 0);
                const length = Number(props.length || 0);
                const thickness = Number(props.thickness || 0);
                return angle === 90
                    ? { x, y, width: thickness, height: length }
                    : { x, y, width: length, height: thickness };
            }

            return null;
        },
        boundsOverlap(a, b) {
            const xOverlap = Math.max(0, Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x));
            const yOverlap = Math.max(0, Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y));
            return xOverlap > 0 && yOverlap > 0;
        },
        getReverseTextBlockWidth(props, variables = []) {
            const content = String(props.content || '');
            const fontWidth = Number(props.fontWidth || props.fontHeight || 10);
            const resolvedContent = content.replace(/«([^»]+)»/g, (match, name) => {
                const variable = variables.find(v => v && v.name === name);
                return variable && variable.defaultValue != null ? String(variable.defaultValue) : match;
            });

            const visibleLength = Math.max(1, resolvedContent.length);
            const naturalWidth = Math.ceil((visibleLength * fontWidth) + Math.max(8, fontWidth * 0.5));
            const currentWidth = Number(props.blockWidth || 0);

            if (!currentWidth || currentWidth < naturalWidth) return naturalWidth;
            return Math.min(currentWidth, naturalWidth + Math.max(16, fontWidth));
        },
        simulatedSaveTemplateToDB(jsonString) {
            return new Promise((resolve) => {
                setTimeout(() => {
                    const id = this.editorRecordId;
                    localStorage.setItem(`prf-customer-template-${id}`, jsonString);
                    console.log(`[BD SQL SERVER SIMULADA] Guardado exitoso de NVARCHAR(MAX) para ID ${id}:`, jsonString);
                    resolve(true);
                }, 800);
            });
        },
        simulatedFetchTemplateFromDB() {
            return new Promise((resolve) => {
                setTimeout(() => {
                    const id = this.editorRecordId;

                    // Si el padre ya tiene una plantilla cargada en el campo Template del registro, la usamos preferentemente
                    if (this.labelData && this.labelData.Template) {
                        console.log(`[BD SQL SERVER SIMULADA] Recuperando plantilla directamente del registro (prop labelData) para ID ${id}`);
                        try {
                            const parsed = typeof this.labelData.Template === 'string'
                                ? JSON.parse(this.labelData.Template)
                                : this.labelData.Template;
                            resolve(parsed);
                            return;
                        } catch (e) {
                            console.error("Error parseando plantilla de labelData.Template:", e);
                        }
                    }

                    // Si no, la buscamos en el localStorage (nuestra simulación de BD persistente)
                    const jsonString = localStorage.getItem(`prf-customer-template-${id}`);
                    if (jsonString) {
                        console.log(`[BD SQL SERVER SIMULADA] Recuperado exitoso desde localStorage para ID ${id}:`, jsonString);
                        resolve(JSON.parse(jsonString));
                    } else {
                        console.log(`[BD SQL SERVER SIMULADA] No hay plantilla registrada para ID ${id}. Se usará la plantilla inicial default.`);
                        resolve(null);
                    }
                }, 800);
            });
        },
    }
};
</script>

<style scoped>
.zebra-editor-wrapper {
    position: relative;
    width: 100%;
    height: 100vh;
    background-color: #141414;
    overflow: hidden;
}

.ml-24 {
    margin-left: 4rem !important;
}

.zebra-editor-floating-header {
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    z-index: 100;
    height: 36px;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    padding: 0 16px;
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;
    box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.4);
    border-top: none;
    transition: background-color 0.3s, border-color 0.3s;
}

.zebra-editor-floating-header.dark {
    background-color: #18181b;
    border: 1px solid #27272a;
    border-top: none;
}

.zebra-editor-floating-header.light {
    background-color: #ffffff;
    border: 1px solid #e4e4e7;
    border-top: none;
}

.iframe-container {
    width: 100%;
    height: 100%;
    position: relative;
}

.zebra-editor-iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
}
</style>
